/**
 * The forecast replenishment orders from, in words (UI conventions audit 2026-10-21, F1; ADR 9060:
 * a code defined in our own code has ONE catalogue map, and its id sits behind the «id» button).
 *
 * The pull reads the forecast the registry row `inseason.pull.forecast_model_version` names, and
 * every proposal line, the forecast basis, the run's provenance and a campaign's base demand record
 * that name as a model version id. Four ids reach the platform today, all produced by bata-data:
 *
 *   - `…postseason_unconstrained_recovered.v0` (`pilot_forecast_b1.py`, `MODEL_VERSION_ID`): each
 *     store, article and size's recent weekly demand, stockout weeks recovered, as it is;
 *   - `…seasonal_index.v1` (armed 2026-10-21, bata-data ADR 6008): the same, scaled by a year-ago
 *     seasonal index — the same weeks of the past years;
 *   - `…store_article_curve.v1` (`store_article_forecast_b1.py`, owner ruling 2026-10-23): one
 *     forecast per store × article, scaled by the same seasonal index, its sizes shared by the
 *     article's size curve;
 *   - `…store_article_curve.v2` (owner approval 2026-10-24, curve "2b"; CO's live pull since
 *     2026-10-25): the same, the size curve sharpened by the store's own sales of each size.
 *
 * The words say WHICH, and that the forecast is adjusted for seasonality. They carry no number. The
 * index's value is published beside the run, not here: the run provenance read's `seasonal_index`
 * (bata-platform ADR 6217), shown by `./seasonal-index.ts` on the run provenance page and in the
 * «Perché» footer. An id this map does not know reads «Forecast not yet described», never a meaning
 * guessed from its segments.
 */

/** Each known forecast id to its words. One literal per key, so the catalogue check sees each. */
export const FORECAST_MODEL_LABEL_KEY: Readonly<Record<string, string>> = {
  'inseason.pull.pilot.postseason_unconstrained_recovered.v0': 'inseason.forecastModel.recentWeeks',
  'inseason.pull.pilot.seasonal_index.v1': 'inseason.forecastModel.recentWeeksSeasonal',
  'inseason.pull.pilot.store_article_curve.v1': 'inseason.forecastModel.storeArticleCurve',
  'inseason.pull.pilot.store_article_curve.v2': 'inseason.forecastModel.storeArticleCurveSharpened',
};

/** What a forecast this build has no words for is called; its id is behind the «id» beside it. */
export const FORECAST_MODEL_UNNAMED_KEY = 'inseason.forecastModel.unnamed';

/** The catalogue key for a forecast id: its words, or «not yet described». */
export function forecastModelKey(modelVersionId: string): string {
  return Object.prototype.hasOwnProperty.call(FORECAST_MODEL_LABEL_KEY, modelVersionId)
    ? FORECAST_MODEL_LABEL_KEY[modelVersionId] ?? FORECAST_MODEL_UNNAMED_KEY
    : FORECAST_MODEL_UNNAMED_KEY;
}

/** Whether this build has words for the id. */
export function forecastModelKnown(modelVersionId: string): boolean {
  return forecastModelKey(modelVersionId) !== FORECAST_MODEL_UNNAMED_KEY;
}

/** A forecast id in words. Never the id: the caller puts that behind an «id». */
export function forecastModelWords(t: (key: string) => string, modelVersionId: string): string {
  return t(forecastModelKey(modelVersionId));
}

/**
 * The workbook's words for a forecast id column (ADR 9041): the words where this build has them,
 * the id itself where it has none — a file is read without the «id» button beside it.
 */
export function forecastModelExportDisplay(code: string, translate: (key: string) => string): string {
  if (!forecastModelKnown(code)) return code;
  const key = forecastModelKey(code);
  const text = translate(key);
  return text === '' || text === key ? code : text;
}
